import React, { useState, useEffect } from "react";
import axios from "axios";
import API_BASE_URL from "../../config/api";
import { useAuth } from "../../context/AuthContext";
import { Link } from "react-router-dom";
import { FiBarChart2, FiZap, FiArrowRight, FiEye, FiGitBranch, FiAward, FiEdit, FiAlertTriangle, FiX, FiPhone } from "react-icons/fi";
import { ZoneIcon } from "../../components/ZoneIcon";
import CategoryZoneGrid from "./CategoryZoneGrid";

import WellbeingAnalysis from "./WellbeingAnalysis";
import ConsolidatedView from "./ConsolidatedView";
import EntryDetailModal, { Entry } from "./EntryDetailModal";
import { getZoneColor, formatZoneName } from "../../utils/zoneUtils";

// ─── Types ─────────────────────────────────────────────────────────────────────

interface PeriodData {
  count: number;
  dominantZone: string | null;
  zoneDistribution: Record<string, number>;
}

interface DashboardStats {
  lastLoggedZone: string | null;
  lastLoggedDate: string | null;
  lastLoggedTime: string | null;
  loggedToday: boolean;
  moodStreak: number;
  weeklyProgress: number;
  monthlyGoal: number;
  monthlyProgress: number;
  currentMonthZone: string | null;
  lastMonthZone: string | null;
  rewards: number;
  periods: {
    last7: PeriodData;
    last30: PeriodData;
    last90: PeriodData;
  };
}

type PeriodKey = "last7" | "last30" | "last90";
type ViewTab = "category-zone" | "journey" | "wellbeing" | "consolidated";
type TimePeriod = "current-month" | "last-month" | "quarter" | "year" | "all" | "custom";

// ─── Constants ─────────────────────────────────────────────────────────────────

// Emoji field removed — ZoneIcon component is now the single source of truth for zone icons.
const ZONE_META: Record<string, { color: string; bg: string; border: string }> = {
  "Green Zone":  { color: getZoneColor("Green Zone").color,  bg: "#f0fdf4", border: "#86efac" },
  "Blue Zone":   { color: getZoneColor("Blue Zone").color,   bg: "#eff6ff", border: "#93c5fd" },
  "Yellow Zone": { color: getZoneColor("Yellow Zone").color, bg: "#fefce8", border: "#fde047" },
  "Brown Zone":  { color: getZoneColor("Brown Zone").color,  bg: "#fef3c7", border: "#d97706" },
  "Red Zone":    { color: getZoneColor("Red Zone").color,    bg: "#fef2f2", border: "#fca5a5" },
  "Dark Red":    { color: getZoneColor("Dark Red").color,    bg: "#fef2f2", border: "#fca5a5" },
};

const VIEW_TABS: { key: ViewTab; label: string }[] = [
  { key: "category-zone", label: "Category / Zone Grid" },
  { key: "consolidated", label: "Consolidated View" },
  { key: "wellbeing", label: "Wellbeing Analysis" },
];

const TIME_PERIODS: { key: TimePeriod; label: string }[] = [
  { key: "current-month", label: "Current Month" },
  { key: "last-month", label: "Last Month" },
  { key: "quarter", label: "Quarter" },
  { key: "year", label: "Year" },
  { key: "all", label: "All" },
  { key: "custom", label: "Custom Selection" },
];

// ─── Helpers ───────────────────────────────────────────────────────────────────

const getZoneMeta = (zone: string | null) => {
  const norm = formatZoneName(zone);
  return norm && ZONE_META[norm] ? ZONE_META[norm] : { color: "#6b7280", bg: "#f9fafb", border: "#d1d5db" };
};

const formatDateTime = (date: string | null, time: string | null): string => {
  if (!date) return "—";
  const d = new Date(`${date}T${time || "00:00:00"}`);
  return (
    d.toLocaleDateString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric" }) +
    ", " +
    d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })
  );
};

const getGreeting = (): string => {
  const h = new Date().getHours();
  if (h < 12) return "Good Morning";
  if (h < 17) return "Good Afternoon";
  return "Good Evening";
};

// ─── Sub-components ────────────────────────────────────────────────────────────

const ZoneCard: React.FC<{ label: string; zone: string | null }> = ({ label, zone }) => {
  const meta = getZoneMeta(zone);
  return (
    <div className="rounded-xl p-6 flex flex-col gap-4 border" style={{ background: meta.bg, borderColor: meta.border }}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-gray-600 uppercase tracking-wide">{label}</span>
        {/* ZoneIcon is the single source of truth for zone icons — no raw emoji */}
        <ZoneIcon zone={zone} size={28} />
      </div>
      <div className="text-xl font-bold tracking-tight" style={{ color: meta.color }}>{formatZoneName(zone) || "No logs"}</div>
    </div>
  );
};

const PeriodToggle: React.FC<{ active: PeriodKey; onChange: (k: PeriodKey) => void }> = ({ active, onChange }) => (
  <div className="flex rounded-lg overflow-hidden border border-gray-200 text-sm font-semibold">
    {(["last7", "last30", "last90"] as PeriodKey[]).map((k, i) => (
      <button
        key={k}
        onClick={() => onChange(k)}
        className="px-4 py-2 transition-colors"
        style={{
          background: active === k ? "#1ecab8" : "#fff",
          color: active === k ? "#fff" : "#6b7280",
          borderRight: i < 2 ? "1px solid #e5e7eb" : undefined,
        }}
      >
        {k === "last7" ? "7D" : k === "last30" ? "30D" : "3MO"}
      </button>
    ))}
  </div>
);

/** Period filter row used by both primary and compare rows */
const PeriodFilterRow: React.FC<{
  label: string;
  active: TimePeriod;
  onChange: (p: TimePeriod) => void;
  dark?: boolean;
  startDate?: string;
  endDate?: string;
  onStartChange?: (v: string) => void;
  onEndChange?: (v: string) => void;
}> = ({ label, active, onChange, dark = false, startDate, endDate, onStartChange, onEndChange }) => (
  <div className="flex flex-wrap items-center gap-3">
    <span className="text-sm font-bold text-gray-500 uppercase tracking-widest shrink-0 whitespace-nowrap">{label}</span>
    <div className="flex flex-wrap items-center gap-1.5">
      {TIME_PERIODS.map(({ key, label: lbl }) => {
        const isActive = active === key;
        return (
          <button
            key={key}
            onClick={() => onChange(key)}
            className="px-3.5 py-1.5 rounded-full text-[13px] font-semibold transition-all whitespace-nowrap"
            style={
              isActive
                ? { background: dark ? "#1a1a2e" : "#1ecab8", color: "#fff" }
                : { background: "transparent", color: "#6b7280" }
            }
          >
            {lbl}
          </button>
        );
      })}
      
      {/* Show custom date pickers globally when Custom Selection is active */}
      {active === "custom" && onStartChange && onEndChange && (
        <div className={`flex items-center gap-1.5 px-3 py-1 ml-2 rounded-xl border ${dark ? 'border-gray-700 bg-gray-800 text-gray-300' : 'border-gray-200 bg-white shadow-sm'} flex-shrink-0`}>
          <input
            type="date"
            value={startDate}
            onChange={(e) => onStartChange(e.target.value)}
            className={`text-[11px] border-0 outline-none bg-transparent ${dark ? 'text-gray-300' : 'text-gray-600'}`}
          />
          <span className={`text-xs ${dark ? 'text-gray-500' : 'text-gray-300'}`}>→</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => onEndChange(e.target.value)}
            className={`text-[11px] border-0 outline-none bg-transparent ${dark ? 'text-gray-300' : 'text-gray-600'}`}
          />
        </div>
      )}
    </div>
  </div>
);

// ─── Main Component ────────────────────────────────────────────────────────────

const StudentDashboard: React.FC = () => {
  const { user } = useAuth();

  // Dashboard stats
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  // Stat card period toggle
  const [activePeriod, setActivePeriod] = useState<PeriodKey>("last7");

  // Analytical report state
  const [activeTab, setActiveTab] = useState<ViewTab>("category-zone");
  const [primaryPeriod, setPrimaryPeriod] = useState<TimePeriod>("current-month");
  const [comparePeriod, setComparePeriod] = useState<TimePeriod>("quarter");
  const [showCompare, setShowCompare] = useState(false);
  
  // Custom date selection state
  const [primaryStartDate, setPrimaryStartDate] = useState("");
  const [primaryEndDate, setPrimaryEndDate] = useState("");
  const [compareStartDate, setCompareStartDate] = useState("");
  const [compareEndDate, setCompareEndDate] = useState("");

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalEntries, setModalEntries] = useState<Entry[]>([]);

  // SOS State
  const [isSosModalOpen, setIsSosModalOpen] = useState(false);
  const [isSosLoading, setIsSosLoading] = useState(false);
  const [sosSuccess, setSosSuccess] = useState(false);

  const openEntryDetail = async (params: { zone?: string; categoryId?: number; date?: string; period?: string }) => {
    if (!user?.id) return;
    try {
      const res = await axios.get(`${API_BASE_URL}/student-mood-logs/cell-logs/${user.id}`, { params });
      if (res.data.logs && res.data.logs.length > 0) {
        setModalEntries(res.data.logs);
        setIsModalOpen(true);
      }
    } catch (e) {
      console.error("Failed to fetch entry details", e);
    }
  };

  useEffect(() => {
    if (!user?.id) return;
    setLoading(true);
    axios
      .get(`${API_BASE_URL}/student-mood-logs/dashboard-stats/${user.id}`)
      .then((r) => setStats(r.data))
      .catch((e) => console.error("Dashboard stats error:", e))
      .finally(() => setLoading(false));
  }, [user?.id]);

  const handleSosTrigger = async () => {
    if (!user || !user.id) return;
    setIsSosLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/sos/trigger`, { studentId: user.id });
      setSosSuccess(true);
      setTimeout(() => {
        setIsSosModalOpen(false);
        setSosSuccess(false);
      }, 3000);
    } catch (err) {
      console.error("SOS trigger failed:", err);
      alert("Failed to send SOS. Please call emergency services directly.");
    } finally {
      setIsSosLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-[#1ecab8] border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-gray-500">Loading dashboard…</span>
        </div>
      </div>
    );
  }

  const firstName = user?.firstName || "Student";
  const lastZoneMeta = getZoneMeta(stats?.lastLoggedZone ?? null);
  const periodData = stats?.periods[activePeriod];

  return (
    <div className="p-6 md:p-8 bg-gray-50 min-h-screen relative">

      {/* ── Task 1: Smart Notification Banners ──────────────────────────────── */}
      <div className="flex flex-col gap-2 mb-6">
        {/* Banner 1 — only shown if NOT logged today */}
        {!stats?.loggedToday && (
          <div className="flex items-center justify-between bg-white border-l-4 border-amber-400 rounded-xl px-5 py-3.5 shadow-sm">
            <div>
              <p className="text-sm font-semibold text-gray-800">If you haven't logged your mood today</p>
              <p className="text-xs text-gray-500 mt-0.5">Takes about 30 seconds and keeps your streak going.</p>
            </div>
            <Link
              to="/student/log-mood"
              className="flex items-center gap-1.5 text-xs font-bold text-[#1ecab8] hover:underline whitespace-nowrap ml-4"
            >
              Log mood <FiArrowRight size={13} />
            </Link>
          </div>
        )}

        {/* Banner 2 — always shown */}
        <div className="flex items-center justify-between bg-white border-l-4 border-rose-400 rounded-xl px-5 py-3.5 shadow-sm">
          <div>
            <p className="text-sm font-semibold text-gray-800">Are you up for a quick chat?</p>
            <p className="text-xs text-gray-500 mt-0.5">Take a moment for yourself. Talking to someone you trust can help.</p>
          </div>
          <Link
            to="/student/community"
            className="flex items-center gap-1.5 text-xs font-bold text-[#1ecab8] hover:underline whitespace-nowrap ml-4"
          >
            Open community <FiArrowRight size={13} />
          </Link>
        </div>
      </div>

      {/* ── Hero Card ────────────────────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8 mb-6">
        <div className="flex flex-col lg:flex-row justify-between gap-6">
          <div className="flex-1">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">
              {getGreeting()}, {firstName}!{" "}
              {/* green status dot — CSS circle is consistent across all platforms */}
              <span className="inline-block w-3 h-3 rounded-full bg-green-500 align-middle mb-0.5" title="Active" aria-label="Active" />
            </h1>
            <p className="text-sm text-gray-500 mb-4">
              Last logged zone:{" "}
              <span className="font-semibold" style={{ color: lastZoneMeta.color }}>
                {stats?.lastLoggedZone || "No logs yet"}
              </span>{" "}
              {stats?.lastLoggedDate && (
                <span className="text-gray-400">{formatDateTime(stats.lastLoggedDate, stats.lastLoggedTime)}</span>
              )}{" "}
              {!stats?.loggedToday && (
                <span className="text-red-400 font-medium">— not logged today yet</span>
              )}
            </p>
            <div className="flex flex-wrap gap-8 mt-2">
              <div>
                <div className="text-sm text-gray-500 mb-1 font-medium">This Week</div>
                <div className="text-2xl font-black text-gray-900 tracking-tight">{stats?.weeklyProgress ?? 0}%</div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1 font-medium">Monthly Goal</div>
                <div className="text-2xl font-black text-gray-900 tracking-tight">
                  {stats?.monthlyProgress ?? 0}/{stats?.monthlyGoal ?? 30}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1 font-medium">Mood Streak</div>
                <div className="text-2xl font-black text-gray-900 tracking-tight">🔥 {stats?.moodStreak ?? 0} days</div>
              </div>
            </div>
          </div>

          <div
            className="self-start rounded-2xl border-2 px-7 py-5 min-w-[200px] shadow-sm"
            style={{ background: lastZoneMeta.bg, borderColor: lastZoneMeta.border }}
          >
            <div className="flex flex-col items-end gap-1.5 text-right">
              <span className="text-xs font-bold uppercase tracking-widest text-gray-500">Last Logged</span>
              <ZoneIcon zone={stats?.lastLoggedZone} size={36} />
              <span className="text-xl font-black tracking-tight" style={{ color: lastZoneMeta.color }}>
                {stats?.lastLoggedZone || "—"}
              </span>
              {stats?.lastLoggedDate && (
                <span className="text-xs font-medium text-gray-500">
                  {formatDateTime(stats.lastLoggedDate, stats.lastLoggedTime)}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Stat Cards Grid ───────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        {/* Last N Days */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-semibold text-gray-700">
              Last {activePeriod === "last7" ? "7" : activePeriod === "last30" ? "30" : "90"} Days
            </span>
            <PeriodToggle active={activePeriod} onChange={setActivePeriod} />
          </div>
          {periodData && periodData.count > 0 ? (
            <>
              <div className="text-3xl font-bold text-gray-900 mb-1">{periodData.count}</div>
              <div className="text-xs text-gray-400 mb-3">mood logs</div>
              <div className="flex flex-wrap gap-1">
                {Object.entries(
                  Object.entries(periodData.zoneDistribution).reduce((acc, [zoneStr, cnt]) => {
                    const zone = formatZoneName(zoneStr);
                    acc[zone] = (acc[zone] || 0) + (cnt as number);
                    return acc;
                  }, {} as Record<string, number>)
                ).map(([zone, cnt]) => {
                  const m = getZoneMeta(zone);
                  return (
                    <span
                      key={zone}
                      className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
                      style={{ background: m.bg, color: m.color, border: `1px solid ${m.border}` }}
                    >
                      {zone.replace(" Zone", "")} ({cnt})
                    </span>
                  );
                })}
              </div>
            </>
          ) : (
            <p className="text-sm text-gray-400 mt-2">No logs in this period</p>
          )}
        </div>

        {/* Current Month Zone */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <span className="text-sm font-semibold text-gray-700 block mb-3">Current Month Zone</span>
          {stats?.monthlyProgress ? (
            <ZoneCard label={`${stats.monthlyProgress} logs this month`} zone={stats.currentMonthZone} />
          ) : (
            <p className="text-sm text-gray-400 mt-2">No logs this month</p>
          )}
        </div>

        {/* Last Month Zone */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <span className="text-sm font-semibold text-gray-700 block mb-3">Last Month Zone</span>
          <ZoneCard label="Predominant zone" zone={stats?.lastMonthZone ?? null} />
        </div>

        {/* Rewards */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-semibold text-gray-700">Rewards</span>
            <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center">
              <FiAward size={18} className="text-blue-600" aria-label="Rewards" />
            </div>
          </div>
          <div className="text-4xl font-bold text-gray-900 mb-1">{stats?.rewards ?? 0}</div>
          <div className="text-xs text-gray-400">earned</div>
        </div>
      </div>

      {/* ── Task 2: Section Header ─────────────────────────────────────────────── */}
      <div className="flex items-center gap-2 mb-4">
        <FiBarChart2 size={20} className="text-gray-500" />
        <h2 className="text-lg font-bold text-gray-800">My Analytical Report</h2>
      </div>

      {/* ── Analytical Report: Single Card with Tabs ───────────────────────── */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 mb-6">

        {/* ── Row 1: Tab Navigation ── screenshot style (pill active, text inactive) ── */}
        <div className="flex items-center justify-start sm:justify-center border-b border-gray-100 p-3 overflow-x-auto gap-2 sm:gap-8">
          {VIEW_TABS.map(({ key, label }) => {
            const isActive = activeTab === key;
            return (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`px-8 py-3 text-[11px] md:text-xs font-bold uppercase tracking-widest whitespace-nowrap transition-all flex-shrink-0 rounded-xl ${
                  isActive
                    ? "bg-[#1ecab8] text-white shadow-sm"
                    : "text-gray-400 hover:text-gray-600 bg-transparent"
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>

        {/* ── Row 2: Shared Period Filter + Actions ────────────────────────── */}
        <div className="px-6 py-3 border-b border-gray-50">
          <div className="flex flex-wrap items-center gap-6">
            <div className="flex-shrink-0">
              <PeriodFilterRow
                label="Primary:"
                active={primaryPeriod}
                onChange={setPrimaryPeriod}
                startDate={primaryStartDate}
                endDate={primaryEndDate}
                onStartChange={setPrimaryStartDate}
                onEndChange={setPrimaryEndDate}
              />
            </div>
            
            <div className="h-6 w-px bg-gray-200 hidden md:block"></div>

            <div className="flex items-center gap-3 shrink-0">
              <Link
                to="/student/mood-history"
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold text-[#1ecab8] border border-[#1ecab8] hover:bg-[#f0fdfb] transition-colors whitespace-nowrap"
              >
                <FiEye size={14} /> View Logs
              </Link>
              {activeTab === "wellbeing" && (
                <button
                  onClick={() => setShowCompare((v) => !v)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold transition-all whitespace-nowrap ${
                    showCompare
                      ? "bg-[#1ecab8] text-white border border-[#1ecab8]"
                      : "border border-gray-200 text-gray-500 hover:bg-gray-50"
                  }`}
                >
                  <FiGitBranch size={14} /> Compare
                </button>
              )}
            </div>
          </div>

          {showCompare && activeTab === "wellbeing" && (
            <div className="mt-2 pt-2 border-t border-gray-50">
              <PeriodFilterRow
                label="Compare vs:"
                active={comparePeriod}
                onChange={setComparePeriod}
                dark
                startDate={compareStartDate}
                endDate={compareEndDate}
                onStartChange={setCompareStartDate}
                onEndChange={setCompareEndDate}
              />
            </div>
          )}
        </div>

        {/* ── Row 3: Active View (ONE at a time) ───────────────────────────── */}
        <div className="p-6 min-h-[680px]">

          {activeTab === "category-zone" && (
            <CategoryZoneGrid
              studentId={user!.id}
              primaryPeriod={primaryPeriod as any}
              comparePeriod={comparePeriod as any}
              showCompare={false}
              onCellClick={(zone, categoryId) => openEntryDetail({ zone, categoryId, period: primaryPeriod })}
              primaryStartDate={primaryStartDate}
              primaryEndDate={primaryEndDate}
              compareStartDate={compareStartDate}
              compareEndDate={compareEndDate}
            />
          )}

          {activeTab === "wellbeing" && (
            <WellbeingAnalysis
              studentId={user!.id}
              primaryPeriod={primaryPeriod as any}
              comparePeriod={comparePeriod as any}
              showCompare={showCompare}
              primaryStartDate={primaryStartDate}
              primaryEndDate={primaryEndDate}
              compareStartDate={compareStartDate}
              compareEndDate={compareEndDate}
            />
          )}

          {activeTab === "consolidated" && (
            <ConsolidatedView
              studentId={user!.id}
              primaryPeriod={primaryPeriod as any}
              comparePeriod={comparePeriod as any}
              showCompare={false}
              onTimelineClick={(date) => openEntryDetail({ date })}
              primaryStartDate={primaryStartDate}
              primaryEndDate={primaryEndDate}
              compareStartDate={compareStartDate}
              compareEndDate={compareEndDate}
            />
          )}

        </div>
      </div>

      <EntryDetailModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        entries={modalEntries}
      />

      {/* ── Task 10: What to do next ──────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-7 h-7 rounded-lg bg-yellow-100 flex items-center justify-center">
            <FiZap size={16} className="text-yellow-500" />
          </div>
          <h2 className="text-base font-bold text-gray-800">What to do next</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
          {[
            { to: "/student/log-mood",      Icon: FiEdit,     bg: "bg-teal-50",   iconCls: "text-teal-600",   label: "Log Today's Mood",  desc: "Track how you're feeling right now" },
            { to: "/student/mood-history",  Icon: FiBarChart2, bg: "bg-blue-50",   iconCls: "text-blue-600",   label: "View Mood History", desc: "Review your past mood entries" },
            { to: "/student/streaks-rewards", Icon: FiAward,   bg: "bg-purple-50", iconCls: "text-purple-600", label: "Check Streaks",     desc: "See your consistency rewards" },
          ].map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="flex items-center gap-3 p-4 rounded-xl border border-gray-100 hover:border-[#1ecab8] hover:bg-[#f0fdfb] transition-all group"
            >
              <div className={`w-10 h-10 rounded-xl ${item.bg} flex items-center justify-center group-hover:scale-110 transition-transform`}>
                <item.Icon size={18} className={item.iconCls} aria-hidden />
              </div>
              <div>
                <div className="text-sm font-semibold text-gray-800">{item.label}</div>
                <div className="text-xs text-gray-400">{item.desc}</div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* ── Fixed SOS Button ─────────────────────────────────────────────────── */}
      <button
        title="SOS - Emergency Support"
        onClick={() => setIsSosModalOpen(true)}
        className="fixed bottom-6 right-6 w-14 h-14 rounded-full bg-red-500 hover:bg-red-600 active:scale-95 text-white font-bold text-sm shadow-lg shadow-red-200 transition-all flex items-center justify-center z-50"
      >
        SOS
      </button>

      {/* ── SOS Modal ────────────────────────────────────────────────────────── */}
      {isSosModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="bg-red-50 p-6 flex flex-col items-center text-center relative border-b border-red-100">
              <button 
                onClick={() => setIsSosModalOpen(false)}
                className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-red-100 text-red-600 hover:bg-red-200 transition-colors"
              >
                <FiX size={18} />
              </button>
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4 shadow-inner">
                <FiAlertTriangle size={32} className="text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-red-700 mb-2">Emergency SOS</h2>
              <p className="text-sm text-red-600/80">
                Are you in immediate danger or experiencing a crisis?
              </p>
            </div>
            
            <div className="p-6">
              {sosSuccess ? (
                <div className="text-center py-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <FiZap size={24} className="text-green-600" />
                  </div>
                  <h3 className="font-bold text-gray-800 text-lg">Alert Sent!</h3>
                  <p className="text-sm text-gray-500 mt-2">Help is on the way. A counselor has been notified.</p>
                </div>
              ) : (
                <>
                  <button
                    onClick={handleSosTrigger}
                    disabled={isSosLoading}
                    className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-4 rounded-xl shadow-md shadow-red-200 transition-all active:scale-[0.98] disabled:opacity-70 flex items-center justify-center gap-2 mb-6"
                  >
                    {isSosLoading ? "Sending Alert..." : "Yes, I need help right now"}
                  </button>

                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Immediate Contacts</h4>
                    <div className="space-y-3">
                      <a href="tel:911" className="flex items-center gap-3 p-3 rounded-lg bg-white border border-gray-100 hover:border-red-200 transition-colors">
                        <div className="w-8 h-8 rounded-full bg-red-50 text-red-600 flex items-center justify-center">
                          <FiPhone size={14} />
                        </div>
                        <div>
                          <div className="font-semibold text-gray-800 text-sm">Emergency Services</div>
                          <div className="text-xs text-red-500 font-bold">911</div>
                        </div>
                      </a>
                      <a href="tel:988" className="flex items-center gap-3 p-3 rounded-lg bg-white border border-gray-100 hover:border-purple-200 transition-colors">
                        <div className="w-8 h-8 rounded-full bg-purple-50 text-purple-600 flex items-center justify-center">
                          <FiPhone size={14} />
                        </div>
                        <div>
                          <div className="font-semibold text-gray-800 text-sm">Crisis Lifeline</div>
                          <div className="text-xs text-purple-500 font-bold">988</div>
                        </div>
                      </a>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentDashboard;
